/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable sonarjs/no-duplicate-string */
import jwt_decode from 'jwt-decode';
import * as EmailValidator from 'email-validator';
import { camelCase, camelCaseTransformMerge } from 'camel-case';
import { parse, format as formatDtFns, isValid } from 'date-fns';
import { APIEnvs, ApiOriginType } from '../types/Config';
import Bundle from '../util/bundle';

export type ConstantsType = {
  [key: string]: string | ConstantsType;
};

type EnvConstantsType = Partial<{
  [key in APIEnvs]: ConstantsType;
}>;

const LIST_OF_KEYS: EnvConstantsType = {};

const dtFmtyyyMMdd = 'yyyy-MM-dd';

export const updateConstants = (
  environment: APIEnvs,
  dados: ConstantsType,
): boolean => {
  LIST_OF_KEYS[environment] = dados;
  return true;
};

/**
 *  @param 'googleMaps' | 'recaptcha' | 'auth0MtoM'
 *  @example getConstants('googleMaps', 'auth0MtoM') ou getConstants()
 */
export const getConstants = (keyConstant?: string): ConstantsType => {
  const config = Bundle.getValue('config');
  const environment = config.service.environment;
  const objConstants: ConstantsType = LIST_OF_KEYS[
    environment as APIEnvs
  ] as ConstantsType;

  if (keyConstant) {
    const entries = Object.entries(objConstants);
    for (const entry of entries) {
      if (entry[0] === keyConstant) {
        return entry[1] as unknown as ConstantsType;
      }
    }
    return {
      message: `Constante não localizada para o canal ${config.channel}`,
    };
  }
  return objConstants;
};

export const getRedirectURL = (key: string): string => {
  let endpoint = '';
  const redirectURL = getConstants('redirectURL');
  if (redirectURL && key in redirectURL) {
    endpoint = redirectURL[key] as string;
  }
  return endpoint;
};

export const isNull = (pValue: unknown): boolean => {
  let isNullAux = false;
  if (pValue === undefined || pValue === null) {
    isNullAux = true;
  }
  return isNullAux;
};

export const isValue = (pValue: unknown) => {
  if (Array.isArray(pValue)) {
    return pValue.length > 0;
  }
  return pValue !== undefined && pValue !== null && pValue !== '';
};

export const toCamelCase = (value: string): string => {
  return camelCase(value, { transform: camelCaseTransformMerge });
};

export const isRangeNumber = (number: number, rangesNumber: number[][]) => {
  let isInRange = false;

  for (const [min, max] of rangesNumber) {
    if (number >= min && number <= max) {
      isInRange = true;
      break;
    }
  }
  return isInRange;
};

export const decodeFromToken = (key: string, token: string) => {
  const decoded: any = jwt_decode(token);
  return decoded[key] ?? '';
};

export const formatAuthenticationScheme = (
  token: string,
  apiType: ApiOriginType,
) => {
  if (apiType === 'Smiles-OAG') {
    const smilesOagTokenKey = 'https://smiles.api/oag_access_token';
    return `Bearer ${decodeFromToken(smilesOagTokenKey, token)}`;
  } else {
    return `Bearer ${token}`;
  }
};

export const promiseAllSettled = (promises: any) =>
  Promise.all(
    promises.map((p: any) =>
      p
        .then((v: any) => ({
          status: 'fulfilled',
          value: v,
        }))
        .catch((e: any) => ({
          status: 'rejected',
          reason: e,
        })),
    ),
  );

export const isValidCpf = (documentCPF: string): boolean => {
  const cpf = documentCPF.replace(/\.|-/g, '');
  if (!cpf || cpf.length < 11) {
    return false;
  }

  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  ) {
    return false;
  }
  let sum = 0;
  let rest;
  for (let i = 1; i <= 9; i += 1) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
  }
  rest = (sum * 10) % 11;

  if (rest === 10 || rest === 11) {
    rest = 0;
  }

  if (rest !== parseInt(cpf.substring(9, 10), 10)) {
    return false;
  }
  sum = 0;
  for (let i = 1; i <= 10; i += 1) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
  }
  rest = (sum * 10) % 11;

  if (rest === 10 || rest === 11) {
    rest = 0;
  }

  if (rest !== parseInt(cpf.substring(10, 11), 10)) {
    return false;
  }
  return true;
};

export const isValidEmail = (email: string): boolean => {
  if (email.length >= 3 && email.includes('@')) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return (
      re.test(String(email).toLowerCase()) && EmailValidator.validate(email)
    );
  }
  return false;
};

export const isValidMemberNumber = (memberNumber: string): boolean => {
  if (String(memberNumber).length < 9) {
    return false;
  }

  const re = /^\d+$/;
  const isOnlyNumbers = re.test(memberNumber);
  return String(memberNumber).length === 9 && isOnlyNumbers;
};

type validDateType = {
  isValid: boolean;
  message?: string;
  formattedDate?: {
    [dtFmtyyyMMdd]: string;
    'dd/MM/yyyy': string;
  };
};
export const isValidDate = (receivedDate: string): validDateType => {
  if (receivedDate) {
    let normalized = receivedDate.replace(new RegExp('/', 'g'), '');
    normalized = normalized.replace(new RegExp('-', 'g'), '');
    const formatValidDates = ['ddMMyyyy', 'yyyyMMdd'];

    let normalizedDate = null;
    const date = new Date();
    for (const fD of formatValidDates) {
      normalizedDate = parse(normalized, fD, date);
      if (isValid(normalizedDate)) {
        return {
          isValid: true,
          formattedDate: {
            [dtFmtyyyMMdd]: formatDtFns(normalizedDate, dtFmtyyyMMdd),
            'dd/MM/yyyy': formatDtFns(normalizedDate, 'dd/MM/yyyy'),
          },
        };
      }
    }
  }
  return {
    isValid: false,
  };
};

/**
 * @description Map to Object [key, value]
 * @see https://www.codegrepper.com/code-examples/typescript/convert+map+to+object+js
 * */
export const mapToObject = (
  data: Map<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
): Record<string | symbol | number, any> => // eslint-disable-line @typescript-eslint/no-explicit-any
  Array.from(data).reduce(
    (obj, [key, value]) => Object.assign(obj, { [key]: value }), // Be careful! Maps can have non-String keys; object literals can't.
    {},
  );

/**
 * @description Remove duplicate slashes (//) of URL string
 * @example removeDuplicateSlashesURL('https://www.abc.com//v1//page/1') => https://www.abc.com/v1/page/1
 * */
export const removeDuplicateSlashesURL = (URL: string): string =>
  URL.replace(/([^:]\/)\/+/g, '$1');

export function groupArrayBy<T>(xs: T[], key: keyof T): { [key: string]: T[] } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return xs.reduce((rv: any, x: any) => {
    rv[x[key]] = rv[x[key]] || [];
    rv[x[key]].push(x);
    return rv;
  }, {});
}

export const parseNullableNumber = (
  value?: string | null,
): number | undefined => (value ? parseNumber(value) : undefined);

export const parseNumber = (value: string): number =>
  parseFloat(value.replace(',', '.'));

export const parseNullableDate = (
  value: string | null | undefined,
  format = dtFmtyyyMMdd,
): Date | undefined => (value ? parse(value, format, new Date()) : undefined);

export const parseDate = (value: string, format = dtFmtyyyMMdd): Date =>
  parse(value, format, new Date());

const replaceUrl = (
  environment: string,
  item: string,
  isNewEnvironment: boolean,
) => {
  return `${environment}${item
    .replace('-dev', '')
    .replace('.dev', '')
    .replace('-uat', '')
    .replace('.uat', '')
    .replace('-hml', '')
    .replace('.hml', '')
    .replace('.smiles.com.br', '')}${isNewEnvironment ? '-N' : ''}` as APIEnvs;
};

export const getEnvironmentByUrl = (url: string): any => {
  const env: APIEnvs = 'PRD';
  url = url.replace('https://www', '');
  url = url.toLowerCase();
  if (url.includes('dev') || url.includes('uat') || url.includes('hml')) {
    const matchDevOld = url.match(/-dev\d.smiles.com.br/g);
    const matchDevNew = url.match(/.dev\d.smiles.com.br/g);
    if (matchDevOld !== null) {
      return replaceUrl('DEV', matchDevOld[0], false);
    } else if (matchDevNew !== null) {
      return replaceUrl('DEV', matchDevNew[0], true);
    }

    // UAT - HML
    const matchHmltOld = url.match(
      /(-uat\d.smiles.com.br)|(-hml\d.smiles.com.br)/g,
    );
    const matchHmlNew = url.match(
      /(.uat\d.smiles.com.br)|(.hml\d.smiles.com.br)/g,
    );
    if (matchHmltOld !== null) {
      return replaceUrl('HML', matchHmltOld[0], false);
    } else if (matchHmlNew !== null) {
      return replaceUrl('HML', matchHmlNew[0], true);
    }
  }
  return env;
};

export const formatDate = formatDtFns;

/**
 * returns an array with numbers between lower and upper
 * @example
 * range(0,3); // returns [0,1,2]
 * @param {number} lower
 * @param {number} upper
 * @returns {number[]} range
 */
export const range = (lower: number, upper: number): number[] => {
  if (lower > upper) {
    throw new Error('the first arg must be smaller than the second');
  }

  const r: number[] = [];

  while (lower < upper) {
    r.push(lower);
    lower++;
  }

  return r;
};

export default {
  mapToObject,
  getConstants,
  getRedirectURL,
  removeDuplicateSlashesURL,
  toCamelCase,
  promiseAllSettled,
  groupArrayBy,
  formatDate,
  range,
  getEnvironmentByUrl,
  validations: {
    isNull,
    isValue,
    isRangeNumber,
    isValidCpf,
    isValidEmail,
    isValidMemberNumber,
    isValidDate,
  },
  parse: {
    parseNullableNumber,
    parseNumber,
    parseNullableDate,
    parseDate,
  },
};
